import type { ReactNode } from "react";
import { Backdrop, Fade, Paper, Popper, useTheme } from "@mui/material";

export type ScheduleInfoPopperShellProps = {
  anchorEl: HTMLElement | null;
  backdropTestId: string;
  children: ReactNode;
  onClose: () => void;
  /** When set (e.g. 300 for holiday), constrains paper width; omit for layouts without a max width. */
  paperMaxWidth?: number;
  popperId: string | undefined;
  popperOpen: boolean;
};

/**
 * Shared Backdrop + Popper + Fade + Paper chrome for holiday / inactivity info poppers.
 */
export function ScheduleInfoPopperShell({
  anchorEl,
  backdropTestId,
  children,
  onClose,
  paperMaxWidth,
  popperId,
  popperOpen
}: Readonly<ScheduleInfoPopperShellProps>) {
  const theme = useTheme();

  return (
    <Backdrop
      open={popperOpen}
      data-testid={backdropTestId}
      onClick={onClose}
      sx={{ bgcolor: "unset", zIndex: theme.zIndex.drawer + 1 }}
    >
      <Popper
        id={popperId}
        open={popperOpen}
        anchorEl={anchorEl}
        transition
        placement="top"
        sx={{ zIndex: theme.zIndex.drawer + 2 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={300}>
            <Paper
              elevation={8}
              sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 2,
                px: 2,
                py: 1.5,
                minWidth: 200,
                ...(typeof paperMaxWidth === "number" ? { maxWidth: paperMaxWidth } : {}),
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows?.[8] ?? "none"
              }}
            >
              {children}
            </Paper>
          </Fade>
        )}
      </Popper>
    </Backdrop>
  );
}
